import React from 'react';
import { Icon, Label, OverviewHeader } from '@brighterion/br-ui';
import { useTranslation } from 'react-i18next';
import { FieldArray, useFormikContext } from 'formik';
import clsx from 'clsx';
import { isAllFilterConditionsValid } from 'Components/CreateVelocityForm/helper';
import { handleKeyEvent, handleOnClickEvent } from 'Utils';
import FilterCondition from '../Condition';
import VelocityConfiguration from './VelocityConfiguration';
import VelocityType from './velocityType';
import styles from './VelocityParamStatement.css';

type VelocityStatmentProps = {
    velocityConfigData: VelocityConfig;
    disableInputField: boolean;
    parameterData: UseGetParametersResponse;
};

const VelocityParamStatement = ({ disableInputField, velocityConfigData, parameterData }: VelocityStatmentProps) => {
    const { values, isSubmitting } = useFormikContext<ParameterVelocityFormSchema>();
    const { t } = useTranslation();
    const isVisible = !disableInputField;
    const newFilterCondition: CustomFilterConditionObj = {
        parameter: '',
        type: '',
        operator: '',
        value: ''
    };
    const readOnly = disableInputField;
    return (
        <>
            <OverviewHeader dataTestId='velocity-param-stmt' leftTitle={t('Parameter Statement')}>
                <div className={styles.ParameterStatementLabel}>
                    <Label label={t('velocityParamStatementInfo')} id='parameter-statement-info' />
                </div>

                <div className={styles.sectionContainer}>
                    <div className={styles.velocityTypeHeader}>{t('velocityType.sectionHeader')}</div>
                    <div className={styles.configurationContainer}>
                        <VelocityType readOnly={readOnly} />
                    </div>

                    {values.velocityType && (
                        <>
                            <VelocityConfiguration
                                velocityConfigData={velocityConfigData}
                                parameterData={parameterData}
                            />

                            <div className={styles.configurationHeader}>{t('velocityFiltersSectionHeader')}</div>
                            <div className={styles.configurationContainer}>
                                <div className={styles.formGroup}>
                                    <FieldArray
                                        name='filters'
                                        render={(conditionsArrayHelper) => (
                                            <>
                                                {values.filters?.map((_, index) => {
                                                    const handleRemoveCondition = (i: number) => {
                                                        conditionsArrayHelper.remove(i);
                                                    };

                                                    return (
                                                        // eslint-disable-next-line react/no-array-index-key
                                                        <div key={index} className={styles.conditionRow}>
                                                            <FilterCondition
                                                                index={index}
                                                                parameters={parameterData}
                                                                conditionsArrayHelper={conditionsArrayHelper}
                                                            />
                                                            {isVisible && (
                                                                <Icon
                                                                    aria-disabled={index === 0}
                                                                    className={clsx(
                                                                        styles.removeBtn,
                                                                        isSubmitting && styles.removeBtnDisabled
                                                                    )}
                                                                    name='feature-remove'
                                                                    size='md'
                                                                    onClick={handleOnClickEvent(() => {
                                                                        handleRemoveCondition(index);
                                                                    })}
                                                                    onKeyDown={handleKeyEvent(() => {
                                                                        handleRemoveCondition(index);
                                                                    })}
                                                                />
                                                            )}
                                                        </div>
                                                    );
                                                })}

                                                {isVisible && (
                                                    <button
                                                        type='button'
                                                        tabIndex={0}
                                                        aria-disabled={
                                                            values.filters
                                                                ? !isAllFilterConditionsValid(values.filters)
                                                                : false
                                                        }
                                                        disabled={
                                                            values.filters
                                                                ? !isAllFilterConditionsValid(values.filters)
                                                                : false || isSubmitting
                                                        }
                                                        className={styles.addConclusionButton}
                                                        onKeyDown={handleKeyEvent(() => {
                                                            if (
                                                                values.filters &&
                                                                isAllFilterConditionsValid(values.filters) &&
                                                                !isSubmitting
                                                            ) {
                                                                conditionsArrayHelper.push(newFilterCondition);
                                                            }
                                                        })}
                                                        onClick={handleOnClickEvent(() => {
                                                            if (
                                                                values.filters &&
                                                                isAllFilterConditionsValid(values.filters) &&
                                                                !isSubmitting
                                                            ) {
                                                                conditionsArrayHelper.push(newFilterCondition);
                                                            }
                                                        })}
                                                    >
                                                        <Icon name='add-file' size='md' />
                                                        <span>{t('Add Filter')}</span>
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    />
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </OverviewHeader>
        </>
    );
};

export default VelocityParamStatement;
